import {
  createContext,
  useContext,
  FC,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { IPlatformService, Platform } from './types';
import { TizenPlatformService } from './implementations/TizenPlatformService';
import { WebOSPlatformService } from './implementations/WebOSPlatformService';
import { BrowserPlatformService } from './implementations/BrowserPlatformService';
import { AndroidPlatformService } from './implementations/AndroidPlatformService';
import { EmbeddedPlatformService } from './implementations/EmbeddedPlatformService';
import { DesktopPlatformService } from './implementations/DesktopPlatformService';
import { detectPlatform, getPlatformFromEnv } from './utils/detectPlatform';

declare global {
  interface Window {
    __effluxPlatform?: IPlatformService;
  }
}

const PlatformContext = createContext<IPlatformService | null>(null);

export function createPlatformService(platform?: Platform): IPlatformService {
  // Priority: explicit parameter > env variable > runtime detection
  const targetPlatform = platform || getPlatformFromEnv() || detectPlatform();

  switch (targetPlatform) {
    case 'tizen':
      return new TizenPlatformService();
    case 'webos':
      return new WebOSPlatformService();
    case 'android':
    case 'ios':
      return new AndroidPlatformService();
    case 'embedded':
      return new EmbeddedPlatformService();
    case 'windows':
    case 'linux':
      // Wrapper Tauri (Windows/Linux) — mesmo service, OS no construtor.
      return new DesktopPlatformService(targetPlatform);
    case 'browser':
    default:
      return new BrowserPlatformService();
  }
}

interface PlatformProviderProps {
  children: ReactNode;
  platform?: Platform;
}

export const PlatformProvider: FC<PlatformProviderProps> = ({
  children,
  platform,
}) => {
  const platformService = useMemo(
    () => createPlatformService(platform),
    [platform],
  );

  useEffect(() => {
    window.__effluxPlatform = platformService;
    platformService.init();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        platformService.onHide();
      } else {
        platformService.onResume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [platformService]);

  return (
    <PlatformContext.Provider value={platformService}>
      {children}
    </PlatformContext.Provider>
  );
};

export const usePlatform = (): IPlatformService => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
};

export { PlatformContext };
